import { HttpHelper } from "../../common/helper/http-helper-service";
import * as signalR from "@microsoft/signalr";

import { constants } from "../constant/constants";
import { rootStoreConstant } from "../constant/root-action-mutation-getter";
import { Enums } from "../helper/enums";
import { SignalrHelper } from "../helper/signalr-helper";
import { SignalrHubManager } from "./signalr-hub-manager";
import { signalrConstants } from "./signalr-constants";
import PrototypeInstance from "../helper/prototype-instance";
import {
  startPortalPresence,
  stopPortalPresence,
  unregisterPortalPresence,
} from "./portal-presence";
type signalrMessage = {
  GroupName: string;
  MethodName: string;
  Message: string;
  ConnectionId: string | null;
};
export class SignalrServiceClass {
  private _hubConnection: signalR.HubConnection | undefined;
  private _reconnectTimeoutId?: ReturnType<typeof setTimeout>;
  private _accessToken: string;
  private _reConnected: boolean;
  private _signalrType: string;
  private _signoutCalled: boolean;

  // The gateway (Gorelo_Infra_Gateway/AuthHandler/BaseAuth.cs) derives and sets
  // x-ms-signalr-userid itself from the verified token on every request through this policy -
  // nothing sent from here is ever trusted, so nothing is sent.
  private _config = {};

  _registerEvents: (hubConnection: signalR.HubConnection) => void;
  constructor(
    signalrType: string,
    registerEvents: (hubConnection: signalR.HubConnection) => void,
  ) {
    this._signalrType = signalrType;
    this._registerEvents = registerEvents;
    this._signoutCalled = false;
  }

  public async InitializeSignalr() {
    await this.createConnection();
    await this.startConnection();
  }

  // Fix for Vue.prototype.$signalRConnection
  private async createConnection() {
    try {
      const info = await HttpHelper.postDataWithHeader(
        signalrConstants.chat.negotiateUrl,
        null,
        this._config,
      );
      this._accessToken = info.accessToken || info.accessKey;
      if (this._accessToken) {
        console.log("Creating connection");
        info.url = info.url || info.endpoint;
        const options: signalR.IHttpConnectionOptions = {
          accessTokenFactory: async () => {
            console.log("Calling token factory");
            try {
              let info = await HttpHelper.postDataWithHeader(
                signalrConstants.chat.negotiateUrl,
                null,
                this._config,
              );
              console.log("negotiate response", info);
              return info.accessToken;
            } catch (ex) {
              console.error("Error in getting access token in signalr", ex);
            }
            return "";
          },
          transport: signalR.HttpTransportType.WebSockets,
          skipNegotiation: false,
        };

        this._hubConnection = new signalR.HubConnectionBuilder()
          .withUrl(info.url, options)
          .configureLogging(signalR.LogLevel.Information)
          .build();

        this._registerEvents(this._hubConnection);
        this.setHubConnectionEvents();
      }
      // Set ON events here
    } catch (e) {
      console.log("signalr stopped by logout");
      console.log(e);
    }
  }

  private async setHubConnectionEvents() {
    if (this._hubConnection != null) {
      this._hubConnection.onclose(async () => {
        console.log("signalr connection closed...");
        // Not reachable in real time any more: the hub has already cleared this connection's
        // presence entry, so stop re-registering it. Resumes on the next connect.
        stopPortalPresence();
        PrototypeInstance.globalProperties.$store.dispatch(
          rootStoreConstant.action.setSignalrConnectionStatus,
          {
            connected: false,
            connectionOpen: false,
            reConnected: this._reConnected,
          },
        );
        if (this._signoutCalled === false) {
          await this.startConnection();
        }
      });

      this._hubConnection.onreconnecting((error?: Error) => {
        if (error) {
          PrototypeInstance.globalProperties.$store.dispatch(
            rootStoreConstant.action.setSignalrConnectionStatus,
            {
              connected: false,
              connectionOpen: true,
              reConnected: this._reConnected,
            },
          );
        }
      });
    }
  }

  private clearReconnectTimeout() {
    if (this._reconnectTimeoutId != null) {
      clearTimeout(this._reconnectTimeoutId);
      this._reconnectTimeoutId = undefined;
    }
  }

  public async startConnection() {
    try {
      this.clearReconnectTimeout();
      let state =
        this._hubConnection == null
          ? "state undefined"
          : this._hubConnection.state;
      console.log("Connection State: " + state);
      if (
        this._hubConnection != null &&
        this._hubConnection.state == signalR.HubConnectionState.Disconnected
      ) {
        await this._hubConnection.start();
        PrototypeInstance.globalProperties.$store
          .dispatch(rootStoreConstant.action.setSignalrConnectionStatus, {
            connected: true,
            connectionOpen: false,
            reConnected: this._reConnected,
          })
          .then(() => {
            this._reConnected = true;
          });
        console.log("SignalR Connected.");

        await SignalrServiceClass.joinSignalRGroups();
        // Present while connected, like Messenger's RegisterPresence-on-connect. See portal-presence.ts.
        void startPortalPresence(this._hubConnection);
      }
    } catch (err: any) {
      console.log("signalr - error", err);

      this.ReConnectSignalr(false, false, this._reConnected);
    }
  }

  public static async joinSignalRGroups() {
    let serviceProviderId: number =
      PrototypeInstance.globalProperties.$store.getters[
        "ContactModule/getServiceProviderId"
      ];
    await SignalrHubManager.Instance.JoinGroup("Ticket_" + serviceProviderId);
    let contactId: number =
      PrototypeInstance.globalProperties.$store.getters[
        "ContactModule/getContactId"
      ];
    await SignalrHubManager.Instance.JoinGroup(
      "GeneralNotification_" + contactId + "_" + serviceProviderId,
    );
    await SignalrHubManager.Instance.JoinGroup(
      "GeneralNotification_" + serviceProviderId,
    );
    await SignalrHubManager.Instance.JoinGroup("Form_" + serviceProviderId);
    await SignalrHubManager.Instance.JoinGroup("User_" + serviceProviderId);
    await SignalrHubManager.Instance.JoinGroup("Billing_" + serviceProviderId);
    await SignalrHubManager.Instance.JoinGroup("Crm_" + serviceProviderId);
    await SignalrHubManager.Instance.JoinGroup("Settings_" + serviceProviderId);
    await SignalrHubManager.Instance.JoinGroup("Project_" + serviceProviderId);
    console.log("SIGNALR GROUPS JOINED");
  }

  private async invokeSendToOthersSignalr(
    methodName: string,
    data: object,
    groupName: string,
    serviceProviderId: number,
  ) {
    const hub = this._hubConnection;
    if (hub == null) {
      return;
    }
    hub
      .invoke(
        "SendToOthers",
        groupName,
        serviceProviderId,
        methodName,
        JSON.stringify(data),
      )
      .catch(async (error) => {
        console.error(`Colud not send message to others: ${methodName}`);
        let connectioId: string | null = "";
        try {
          connectioId = hub.connectionId;
        } catch (ex) {
          console.error(ex);
        }
        try {
          let signalRMessage: signalrMessage = {
            GroupName: `${groupName}_${serviceProviderId}`,
            MethodName: methodName,
            Message: JSON.stringify(data),
            ConnectionId: connectioId,
          };
          await HttpHelper.postData(signalrConstants.chat.sendMessage, signalRMessage);
        } catch (err) {
          console.error(
            `Colud not send message to others using api call: ${methodName}`,
            err,
          );
        }
      });
  }

  private ReConnectSignalr(
    connectedState: boolean,
    connectionOpenState: boolean,
    reConnectedState: boolean,
  ) {
    this.clearReconnectTimeout();
    this._reconnectTimeoutId = setTimeout(async () => {
      this._reconnectTimeoutId = undefined;
      await this.startConnection();
    }, 5000);
  }

  public async sendTicketDetailSignalr(
    ticketId: any,
    data: object,
    methodName: string,
  ) {
    const groupName = `TicketDetail_${ticketId}`;

    //await this.ensureSignalrConnected(methodName, data);

    await this.sendToOthersSignalr(methodName, data, groupName);
  }

  public async sendTicketEntitySignalr(
    ticketId: string | undefined | null,
    eventId: number = 1,
    tick: number = 0,
  ) {
    if (ticketId) {
      const objSignalR = { entityId: ticketId, eventId: eventId, tick: tick };
      await this.sendToOthers(
        Enums.BaseRole.Ticket,
        constants.signalrMethods.ticket.ticketEntitySync,
        objSignalR,
      );
    } else {
      console.log(`Ticket Id was not set:${ticketId}`);
    }
  }

  public async sendToOthers(
    baseRole: Enums.BaseRole,
    methodName: string,
    data: object,
  ) {
    let serviceProviderId: number =
      PrototypeInstance.globalProperties.$store.getters[
        "ContactModule/getServiceProviderId"
      ];
    let groupName = SignalrHelper.GetSignalrGroupByRole(
      baseRole,
      serviceProviderId,
    );

    if (groupName === "") {
      return;
    }

    groupName = groupName.replace("_" + serviceProviderId, "");
    await this.invokeSendToOthersSignalr(
      methodName,
      data,
      groupName,
      serviceProviderId,
    );
  }

  public async sendToOthersSignalr(
    methodName: string,
    data: object,
    groupName: string,
  ) {
    const serviceProviderId: number =
      PrototypeInstance.globalProperties.$store.getters[
        "ContactModule/getServiceProviderId"
      ];
    await this.invokeSendToOthersSignalr(
      methodName,
      data,
      groupName,
      serviceProviderId,
    );
  }

  public sendToMe(methodName: string, data: object) {
    this.sendToUserById(
      PrototypeInstance.globalProperties.$LoggedInUser.email,
      methodName,
      data,
    );
  }

  public async sendToUserById(
    userEmail: string,
    methodName: string,
    data: object,
  ) {
    if (this._hubConnection != null) {
      try {
        await HttpHelper.postData(signalrConstants.chat.sendMessage, {
          UserId: userEmail,
          MethodName: methodName,
          Message: JSON.stringify(data),
        });
      } catch (err) {
        console.error(
          `Colud not send message to user by id: ${methodName}`,
          err,
        );
      }
    }
  }

  /** Logout: drop this contact's presence, then end the connection without retrying. Without this
   * the connection outlived the session and kept receiving the contact's messages. */
  public async signOutSignalr() {
    // Before the unregister: it makes the server close the connection, and onclose must not
    // reconnect it.
    this._signoutCalled = true;
    await unregisterPortalPresence();
    await this.stopSignalrConnection();
  }

  public async stopSignalrConnection(dontRetry: boolean = false) {
    this.clearReconnectTimeout();
    stopPortalPresence();
    if (!dontRetry) {
      this._signoutCalled = true;
    }
    try {
      if (!this._hubConnection) {
        console.log("Connection instance not found to stop connection");
      }
      await this._hubConnection!.stop();
    } catch (ex) {
      console.error("Error occurred in signalr stop", ex);
    }
    console.error("Connection stop successfully");
  }

  // The server now appends the caller's own verified ServiceProviderId itself
  // (JoinGroupByPrefix/LeaveGroupByPrefix), so callers here keep passing the same full
  // group name they always have and this strips the trailing "_<spid>" back off before
  // invoking - no call site needed to change.
  private static stripServiceProviderIdSuffix(groupName: string): string {
    const serviceProviderId: number =
      PrototypeInstance.globalProperties.$store.getters[
        "ContactModule/getServiceProviderId"
      ];
    const suffix = `_${serviceProviderId}`;
    return groupName.endsWith(suffix) ? groupName.slice(0, -suffix.length) : groupName;
  }

  public joinSignalrGroup(groupName: string) {
    if (this._hubConnection != null) {
      this._hubConnection.invoke("JoinGroupByPrefix", SignalrServiceClass.stripServiceProviderIdSuffix(groupName));
    }
  }

  public leaveSignalrGroup(groupName: string) {
    if (this._hubConnection != null) {
      this._hubConnection.invoke("LeaveGroupByPrefix", SignalrServiceClass.stripServiceProviderIdSuffix(groupName));
    }
  }

  public getSignalrConnection(): signalR.HubConnection | undefined {
    return this._hubConnection;
  }
}
